import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import ServiceModal, { type ServiceForm } from "@/pages/admin/website/services/components/ServiceModal";

/* ─── Types ──────────────────────────────────────────────────── */
export type ServiceRow = {
  id: string;
  tenant_id: string;
  title: string;
  description: string | null;
  price_from: number | null;
  image_url: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

const ICON_MAP: Record<number, string> = {
  0: "ri-camera-3-line",
  1: "ri-video-line",
  2: "ri-image-edit-line",
  3: "ri-disc-line",
  4: "ri-album-line",
  5: "ri-magic-line",
};

interface Props { tenantId: string; }

export default function ServicesTab({ tenantId }: Props) {
  const [services, setServices]   = useState<ServiceRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState<ServiceRow | null>(null);
  const [saving, setSaving]       = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [movingId, setMovingId]     = useState<string | null>(null);

  /* ── Fetch ── */
  const fetchServices = useCallback(async () => {
    const { data } = await supabase
      .from("tenant_services")
      .select("*")
      .eq("tenant_id", tenantId)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    setServices((data as ServiceRow[]) ?? []);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  /* ── Open modal ── */
  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (s: ServiceRow) => { setEditing(s); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditing(null); };

  /* ── Save (create / update) ── */
  const handleSave = async (form: ServiceForm) => {
    setSaving(true);
    const payload = {
      tenant_id:   tenantId,
      title:       form.title.trim(),
      description: form.description.trim() || null,
      price_from:  form.price_from !== "" ? Number(form.price_from) : null,
      image_url:   form.image_url.trim() || null,
      is_active:   form.is_active,
      updated_at:  new Date().toISOString(),
    };

    if (editing) {
      const { error } = await supabase
        .from("tenant_services")
        .update(payload)
        .eq("id", editing.id)
        .eq("tenant_id", tenantId);
      if (error) {
        setSaving(false);
        Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
        return;
      }
    } else {
      const maxOrder = services.reduce((m, s) => Math.max(m, s.sort_order), -1);
      const { error } = await supabase
        .from("tenant_services")
        .insert({ ...payload, sort_order: maxOrder + 1 });
      if (error) {
        setSaving(false);
        Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
        return;
      }
    }

    setSaving(false);
    closeModal();
    fetchServices();
  };

  /* ── Delete ── */
  const handleDelete = async (svc: ServiceRow) => {
    const result = await Swal.fire({
      title: "Delete service?",
      html: `<p style="color:rgba(255,255,255,0.5);font-size:13px">"<strong style='color:#fff'>${svc.title}</strong>" will be permanently removed from your website.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      background: "#ffffff",
      color: "#0f172a",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;

    setDeletingId(svc.id);
    await supabase.from("tenant_services").delete().eq("id", svc.id).eq("tenant_id", tenantId);
    setDeletingId(null);
    fetchServices();
  };

  /* ── Toggle active ── */
  const handleToggle = async (svc: ServiceRow) => {
    await supabase
      .from("tenant_services")
      .update({ is_active: !svc.is_active, updated_at: new Date().toISOString() })
      .eq("id", svc.id)
      .eq("tenant_id", tenantId);
    setServices((prev) =>
      prev.map((s) => s.id === svc.id ? { ...s, is_active: !svc.is_active } : s),
    );
  };

  /* ── Move (sort_order) ── */
  const handleMove = async (id: string, dir: "up" | "down") => {
    const idx = services.findIndex((s) => s.id === id);
    if (dir === "up" && idx === 0) return;
    if (dir === "down" && idx === services.length - 1) return;

    setMovingId(id);
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    const a = services[idx];
    const b = services[swapIdx];

    await Promise.all([
      supabase.from("tenant_services").update({ sort_order: b.sort_order, updated_at: new Date().toISOString() }).eq("id", a.id).eq("tenant_id", tenantId),
      supabase.from("tenant_services").update({ sort_order: a.sort_order, updated_at: new Date().toISOString() }).eq("id", b.id).eq("tenant_id", tenantId),
    ]);

    setMovingId(null);
    fetchServices();
  };

  /* ── Active count ── */
  const activeCount = services.filter((s) => s.is_active).length;

  /* ─── Render ──────────────────────────────────────────────── */
  return (
    <>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-700 font-semibold text-sm">
              {services.length} {services.length === 1 ? "service" : "services"}
            </p>
            <p className="text-slate-400 text-xs">
              {activeCount} published · {services.length - activeCount} hidden
            </p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Add Service
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : services.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 bg-white rounded-xl border border-slate-200 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10">
              <i className="ri-stack-line text-[#C8A96E] text-2xl"></i>
            </div>
            <p className="text-slate-600 text-sm font-medium">No services yet</p>
            <p className="text-slate-400 text-xs max-w-xs">
              Add your photography packages so visitors can see what you offer.
            </p>
            <button
              onClick={openNew}
              className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap mt-1"
            >
              <i className="ri-add-line"></i>
              Add First Service
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {services.map((svc, idx) => (
              <div
                key={svc.id}
                className={`bg-white rounded-xl border transition-all ${
                  svc.is_active
                    ? "border-slate-200 hover:border-slate-300"
                    : "border-slate-100 opacity-60"
                }`}
              >
                <div className="flex items-center gap-4 px-4 py-3.5">
                  {/* Sort arrows */}
                  <div className="flex flex-col items-center gap-0.5 w-7 shrink-0">
                    <button
                      onClick={() => handleMove(svc.id, "up")}
                      disabled={idx === 0 || movingId === svc.id}
                      className="w-5 h-5 flex items-center justify-center text-slate-300 hover:text-slate-500 disabled:opacity-20 cursor-pointer transition-colors"
                    >
                      <i className="ri-arrow-up-s-line text-xs"></i>
                    </button>
                    <span className="text-slate-300 text-[10px] font-mono leading-none">{idx + 1}</span>
                    <button
                      onClick={() => handleMove(svc.id, "down")}
                      disabled={idx === services.length - 1 || movingId === svc.id}
                      className="w-5 h-5 flex items-center justify-center text-slate-300 hover:text-slate-500 disabled:opacity-20 cursor-pointer transition-colors"
                    >
                      <i className="ri-arrow-down-s-line text-xs"></i>
                    </button>
                  </div>

                  {/* Thumbnail */}
                  {svc.image_url ? (
                    <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-slate-100">
                      <img
                        src={svc.image_url}
                        alt={svc.title}
                        className="w-full h-full object-cover object-top"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-lg shrink-0 bg-[#C8A96E]/10 flex items-center justify-center">
                      <i className={`${ICON_MAP[idx % 6]} text-[#C8A96E] text-lg`}></i>
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-slate-800 font-medium text-sm truncate">{svc.title}</p>
                      {!svc.is_active && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-400 border border-slate-200">
                          Hidden
                        </span>
                      )}
                    </div>
                    {svc.description && (
                      <p className="text-slate-400 text-xs mt-0.5 line-clamp-1">{svc.description}</p>
                    )}
                  </div>

                  {/* Price */}
                  <div className="text-right shrink-0 hidden sm:block">
                    {svc.price_from != null ? (
                      <div>
                        <p className="text-slate-400 text-[9px] uppercase tracking-wider">From</p>
                        <p className="text-[#C8A96E] text-sm font-semibold">
                          ${Number(svc.price_from).toLocaleString()}
                        </p>
                      </div>
                    ) : (
                      <span className="text-slate-300 text-xs italic">No price</span>
                    )}
                  </div>

                  {/* Toggle active */}
                  <button
                    type="button"
                    onClick={() => handleToggle(svc)}
                    title={svc.is_active ? "Hide from public" : "Publish"}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer shrink-0 ${
                      svc.is_active ? "bg-emerald-500" : "bg-slate-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                        svc.is_active ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </button>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openEdit(svc)}
                      title="Edit"
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all cursor-pointer"
                    >
                      <i className="ri-edit-2-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(svc)}
                      disabled={deletingId === svc.id}
                      title="Delete"
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all cursor-pointer disabled:opacity-40"
                    >
                      {deletingId === svc.id ? (
                        <div className="w-3.5 h-3.5 border border-red-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <i className="ri-delete-bin-6-line text-sm"></i>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Public hint */}
        {services.length > 0 && (
          <p className="text-slate-400 text-xs text-center pt-1">
            <i className="ri-global-line mr-1"></i>
            Only published services appear on your public website page.
          </p>
        )}
      </div>

      {/* Modal */}
      <ServiceModal
        open={modalOpen}
        editing={editing}
        saving={saving}
        onClose={closeModal}
        onSave={handleSave}
      />
    </>
  );
}